import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { summarizeFile } from '../api/upload'
import { uploadFile } from '../utils/fileUploader'

interface UseFileUploadOptions {
  avatarId: string
  organizationId: string
  onUploadStart?: () => void
  onUploadSuccess?: () => void
  onUploadError?: (error: unknown) => void
  setIsFileUploading?: (isUploading: boolean) => void
  isFileUploading?: boolean
  showToasts?: boolean
  dataChannel?: RTCDataChannel | null
  socketId: string
}

interface UseFileUploadReturn {
  fileInputRef: React.RefObject<HTMLInputElement | null>
  isProcessing: boolean
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf']

export const useFileUpload = ({
  avatarId,
  organizationId,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
  setIsFileUploading,
  isFileUploading,
  dataChannel,
  showToasts = true,
  socketId,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [file, setFile] = useState<File | null>(null)

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const validateFile = useCallback(
    (file: File): boolean => {
      if (file.size > MAX_FILE_SIZE) {
        resetFileInput()
        if (showToasts) {
          toast.warning('File size should be less than 20MB')
        } else {
          console.warn('File size should be less than 20MB')
        }
        setIsFileUploading?.(false)
        return false
      }

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        resetFileInput()
        if (showToasts) {
          toast.warning('Invalid file type')
        } else {
          console.warn('Invalid file type')
        }
        setIsFileUploading?.(false)
        return false
      }

      return true
    },
    [resetFileInput, setIsFileUploading, showToasts],
  )

  const uploadAndSummarizeFile = useCallback(
    async (fileToUpload: File) => {
      try {
        onUploadStart?.()
        const response = await uploadFile(fileToUpload, organizationId)

        if (response?.id) {
          setIsProcessing(true)
          await summarizeFile({
            mentorId: avatarId,
            uploadId: response.id,
            socketId: socketId || '',
          })
        }
        resetFileInput()
      } catch (error) {
        console.error('Error uploading file:', error)
        setIsFileUploading?.(false)

        if (showToasts) {
          toast.error('Error uploading file')
        }

        onUploadError?.(error)
        resetFileInput()
      } finally {
        onUploadSuccess?.()
        setIsProcessing(false)
        setIsFileUploading?.(false)
      }
    },
    [
      avatarId,
      organizationId,
      dataChannel,
      onUploadStart,
      onUploadSuccess,
      onUploadError,
      setIsFileUploading,
      resetFileInput,
      showToasts,
    ],
  )

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target

      if (files && files.length > 0) {
        setIsFileUploading?.(true)
        const newFile = files[0]

        if (!validateFile(newFile)) {
          return
        }

        setFile(newFile)
        return
      }

      setFile(null)
    },
    [validateFile, setIsFileUploading],
  )

  useEffect(() => {
    if (file !== null) {
      uploadAndSummarizeFile(file).catch(error => {
        console.error('Error in file upload:', error)
      })
    }
  }, [file])

  useEffect(() => {
    if (!isFileUploading && isProcessing) {
      setIsProcessing(false)
    }
  }, [isFileUploading, isProcessing])

  return {
    fileInputRef,
    isProcessing,
    handleFileChange,
  }
}
